import {useMemo, useState} from 'react';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {Box, Stack, Typography} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import {translations} from '../../../i18n/translations';
import {ContractAmendment} from '../../../backend/interfaces/contract-amendment';
import {LogsHeader} from '../common/logs-header';
import {LogsLoading} from '../common/logs-loading';
import {LogsEmpty} from '../common/logs-empty';
import {BaseLogAction} from '../common/base-log-action';
import {LogsLoadMoreBtn} from '../common/logs-load-more-btn';
import {useContractAmendmentsUsersCache} from './use-contract-amendments-users-cache';
import '../history-logs.scss';

export const ContractAmendmentLogs = ({
  contractAmendments,
}: {
  contractAmendments?: ContractAmendment[];
}) => {
  const {t} = useTranslation();
  const [itemsNumber, setItemsNumber] = useState(5);

  // Users info cache
  const {users, loading} = useContractAmendmentsUsersCache(contractAmendments);

  // Sort history logs
  const sortedContractAmendments: ContractAmendment[] = useMemo(() => {
    let result: ContractAmendment[] = Array.from(contractAmendments ?? []);

    // Sort by date descending
    result.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return result;
  }, [contractAmendments]);

  return (
    <LogsHeader>
      {loading ? (
        <LogsLoading />
      ) : (
        <Stack>
          {sortedContractAmendments.length === 0 ? (
            <LogsEmpty />
          ) : (
            sortedContractAmendments.map((ca, index) => {
              // itemsNumber is used to limit the number of items displayed
              if (index >= itemsNumber) return null;

              let authorId = ca.authorId;
              const date = moment(ca.createdAt);
              const dateHours = date.format('LT');
              const dateDay = date.format('DD/MM/YYYY');
              const dateStr = `${dateDay} ${t(translations.schedule_field.readable_periodicity.at_hour)} ${dateHours}`;
              const name =
                authorId && users[authorId]
                  ? users[authorId].name
                  : `${t(translations.care_and_activities.handle_task_modal.history.someone)} (${authorId})`;
              const picture = authorId && users[authorId] ? users[authorId].picture : undefined;

              return (
                <BaseLogAction
                  key={`action-${index}`}
                  title={
                    name +
                    ' ' +
                    t(translations.care_and_activities.handle_task_modal.history.contract_update)
                  }
                  name={name}
                  picture={picture}
                  date={dateStr}
                >
                  <Box className="status">
                    <Typography className="label">
                      {t(translations.care_and_activities.handle_task_modal.history.changes)}:
                    </Typography>
                    <AmendmentModifications ca={ca} />
                  </Box>
                </BaseLogAction>
              );
            })
          )}
          {sortedContractAmendments.length > itemsNumber && (
            <LogsLoadMoreBtn onClick={() => setItemsNumber(itemsNumber + 5)} />
          )}
        </Stack>
      )}
    </LogsHeader>
  );
};

// ----------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------

function AmendmentModifications({ca}: {ca: ContractAmendment}) {
  const {t} = useTranslation();

  return (
    <>
      {ca.licensesNumber !== undefined && (
        <Typography className="desc separator">
          {ca.licensesNumber} {t(translations.office.system_administrator.tabs.licenses)}
        </Typography>
      )}
      {ca.includesDigitalCareManagement !== undefined && (
        <Typography className="desc separator">
          DCM{' '}
          {ca.includesDigitalCareManagement ? (
            <CheckIcon style={{color: 'green', fontSize: '17px'}} />
          ) : (
            <ClearIcon style={{color: 'red', fontSize: '17px'}} />
          )}
        </Typography>
      )}
      {ca.includesDigitalInclusion !== undefined && (
        <Typography className="desc separator">
          DI{' '}
          {ca.includesDigitalInclusion ? (
            <CheckIcon style={{color: 'green', fontSize: '17px'}} />
          ) : (
            <ClearIcon style={{color: 'red', fontSize: '17px'}} />
          )}
        </Typography>
      )}
    </>
  );
}
